<template>
  <article :class="classes" :aria-label="ariaLabel">
    <!-- Метка над матчем: дата + ссылка «линия на матч». В figma она
         расположена строго над верхней строкой слота, поверх viewport'а
         сетки. Если ни даты, ни ссылки нет — не рендерим, чтобы не
         сжимать высоту матча. -->
    <header v-if="headerVisible" :class="$style.head">
      <div v-if="dateText || match.line_url" :class="$style.headMeta">
        <span v-if="dateText" :class="$style.date">{{ dateText }}</span>
        <a
          v-if="match.line_url"
          :href="match.line_url"
          target="_blank"
          rel="noopener noreferrer"
          :class="$style.lineLink"
          @click="onLineClick"
        >
          {{ t('lineLink') }}
        </a>
      </div>
      <span v-if="caption" :class="$style.caption">{{ caption }}</span>
    </header>

    <div :class="$style.body">
      <slot-row
        :slot-data="leftDisplaySlot"
        :side="'left'"
        :score="resultsView ? match.score_left : null"
        :is-pick="
          !resultsView &&
          !locked &&
          match.user_pick_team_id === leftDisplaySlot.team?.id
        "
        :is-outcome="!resultsView && match.outcome_side === 'left'"
        :is-correct="
          !resultsView && match.user_pick_team_id === leftDisplaySlot.team?.id
            ? match.is_correct
            : null
        "
        :pickable="canPickSlot(leftSlot)"
        :in-flight="inFlight"
        @pick="onPickSlot(leftSlot)"
      />
      <slot-row
        :slot-data="rightDisplaySlot"
        :side="'right'"
        :score="resultsView ? match.score_right : null"
        :is-pick="
          !resultsView &&
          !locked &&
          match.user_pick_team_id === rightDisplaySlot.team?.id
        "
        :is-outcome="!resultsView && match.outcome_side === 'right'"
        :is-correct="
          !resultsView && match.user_pick_team_id === rightDisplaySlot.team?.id
            ? match.is_correct
            : null
        "
        :pickable="canPickSlot(rightSlot)"
        :in-flight="inFlight"
        @pick="onPickSlot(rightSlot)"
      />
    </div>
  </article>
</template>

<i18n lang="yaml">
ru:
  lineLink: линия на матч
</i18n>

<script lang="ts" setup>
import { computed } from 'vue'
import SlotRow from './MatchSlot.vue'
import { usePickemAnalytics } from '../../../composables/use-pickem-analytics'
import {
  getBracketSlotDisplayTeam,
  toBracketDisplaySlot
} from '../../../utils/bracket-slot-display'
import { isBracketMatchLockedByDownstreamPicks } from '../../../utils/bracket-match-lock'
import type {
  BracketMatch,
  BracketSlot,
  Stage,
  Tournament
} from '../../../types'

defineOptions({ name: 'PickemBracketMatch' })

const props = withDefaults(
  defineProps<{
    match: BracketMatch
    stage: Stage
    tournament: Tournament
    locked?: boolean
    inFlight?: boolean
    resultsView?: boolean
    caption?: string | null
  }>(),
  {
    locked: false,
    inFlight: false,
    resultsView: false,
    caption: null
  }
)

const emit = defineEmits<{
  pick: [payload: { match: BracketMatch; slot: BracketSlot }]
}>()

const { t, locale } = useI18n()
const { trackMatchLineClick } = usePickemAnalytics()

const cssModule = useCssModule()

const leftSlot = computed(
  () => props.match.slots.find(s => s.side === 'left') ?? props.match.slots[0]
)
const rightSlot = computed(
  () => props.match.slots.find(s => s.side === 'right') ?? props.match.slots[1]
)

const leftDisplaySlot = computed(() =>
  toBracketDisplaySlot(leftSlot.value, props.resultsView)
)
const rightDisplaySlot = computed(() =>
  toBracketDisplaySlot(rightSlot.value, props.resultsView)
)

// Метка с датой и линией — по дизайну. Дату форматируем коротко:
// «12 авг 15:00» (день + сокр. месяц + время). У нас в бэке start_at
// присылается unix-timestamp в секундах.
const dateText = computed(() => {
  if (!props.match.starts_at) return null
  const date = new Date(props.match.starts_at * 1000)
  const day = date.getDate()
  const month = new Intl.DateTimeFormat(locale.value, { month: 'short' })
    .format(date)
    .replace('.', '')
  const time = new Intl.DateTimeFormat(locale.value, {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
  return `${day} ${month} ${time}`
})

const headerVisible = computed(() =>
  Boolean(dateText.value || props.match.line_url || props.caption)
)

const classes = computed(() => [
  cssModule.match,
  props.locked && cssModule.locked,
  props.resultsView && cssModule.resultsView
])

const ariaLabel = computed(() => {
  const left = leftDisplaySlot.value.team?.name ?? '—'
  const right = rightDisplaySlot.value.team?.name ?? '—'
  return `${props.match.match_type || `Матч #${props.match.id}`}: ${left} vs ${right}`
})

const lockedByDownstream = computed(() => {
  if (!props.stage.bracket || props.resultsView) return false
  return isBracketMatchLockedByDownstreamPicks(
    props.stage.bracket,
    props.match.id
  )
})

const isMatchPickable = () => {
  if (lockedByDownstream.value) return false
  return props.match.pickable
}

const canPickSlot = (slot: BracketSlot | undefined) => {
  if (!slot) return false
  if (props.locked || props.resultsView) return false
  if (props.inFlight) return false
  if (!isMatchPickable()) return false
  return getBracketSlotDisplayTeam(slot, false) !== null
}

const onPickSlot = (slot: BracketSlot | undefined) => {
  if (!slot) return
  if (!canPickSlot(slot)) return
  emit('pick', { match: props.match, slot })
}

const onLineClick = () => {
  trackMatchLineClick({
    match: props.match,
    stage: props.stage,
    tournament: props.tournament
  })
}
</script>

<style lang="scss" module>
.match {
  // ВНИМАНИЕ: высота строго совпадает с BRACKET_MATCH_HEIGHT_EM в
  // use-bracket-layout (9.2em = 2 × 4.6em), чтобы SVG-связи попадали в
  // центр матча. Header'а нет в потоке — он позиционирован absolute
  // над body, чтобы не сжимать body.
  position: relative;
  width: 22em;
  height: 9.2em;
}

.head {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2em;
  padding: 0 0.4em 0.4em;
  pointer-events: none;
}

.headMeta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.8em;
}

.caption {
  @include typo-get(label-s);
  color: color-get(--root-rgb-text-3);
  text-align: center;
}

.date {
  @include typo-get(label-s);
  color: color-get(--root-rgb-text-1);
  font-weight: 500;
}

.lineLink {
  @include typo-get(label-s);
  color: color-get(--root-rgb-accent-2);
  text-decoration: none;
  font-weight: 500;
  pointer-events: auto;

  @include hover-focus {
    text-decoration: underline;
  }
}

.body {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
